/**
 * What the ceilings beside the model picker are priced for, in one line.
 *
 * A ceiling is priced by its models (`engagement.priced_ceiling`): the depth
 * table is what the work costs on the reference model, and a run on another
 * gets that work's price on its own. The tiles change when a model is picked,
 * and a number that moves with no reason beside it reads as a bug — so this
 * says which models, and by how much against what.
 *
 * Pure, and out of the component, for the reason `credit.ts` is: a sentence
 * with three cases is worth a table of tests, and the page is 1,100 lines.
 */

import { modelLabel } from "./labels";
import type { Ceilings, Options } from "./types";

export function pricedFor(
  ceilings: Ceilings | undefined,
  options: Options | undefined,
): string | null {
  if (!ceilings) return null;
  const lead = modelLabel(options, ceilings.provider, ceilings.model) ?? ceilings.model;
  const sub =
    ceilings.subagent_model && ceilings.subagent_model !== ceilings.model
      ? (modelLabel(options, ceilings.provider, ceilings.subagent_model) ??
        ceilings.subagent_model)
      : null;
  const models = sub ? `${lead}, specialists on ${sub}` : lead;
  const reference = ceilings.reference.label;

  if (ceilings.cost_factor === null) {
    return `No declared price for ${models}, so these are the ${reference} ceilings.`;
  }
  if (ceilings.cost_factor === 1) {
    return `Priced for ${models}, the model these ceilings were set on.`;
  }
  return (
    `Priced for ${models}: the same work costs ` +
    `${ceilings.cost_factor.toFixed(2)}× what it does on ${reference}.`
  );
}
